import {
  COLOR_SCHEMES,
  DARK_THEME_MEDIA_QUERY,
  DEFAULT_SYSTEM_THEME,
} from 'lib/next-themes/constants';
import {
  disableAnimation,
  getSystemTheme,
  getTheme,
} from 'lib/next-themes/helpers';
import type { UseThemeProps, ValueObject } from 'lib/next-themes/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseThemeProviderOptions {
  /** List of all available theme names */
  themes: string[];
  /** Forced theme name for the current page */
  forcedTheme?: string | undefined;
  /** Whether to switch between dark and light themes based on prefers-color-scheme */
  enableSystem: boolean;
  /** Disable all CSS transitions when switching themes */
  disableTransitionOnChange: boolean;
  /** Whether to indicate to browsers which color scheme is used (dark or light) for built-in UI like inputs and buttons */
  enableColorScheme: boolean;
  /** Key used to store theme setting in localStorage */
  storageKey: string;
  /** Default theme name (for v0.0.12 and lower the default was light). If `enableSystem` is false, the default theme is light */
  defaultTheme: string;

  /** HTML attribute modified based on the active theme. Accepts `class` and `data-*` (meaning any data attribute, `data-mode`, `data-color`, etc.) */
  attribute: string | 'class';

  value?: ValueObject | undefined;
}

export default function useThemeProvider({
  attribute,
  disableTransitionOnChange,
  enableSystem,
  enableColorScheme,
  forcedTheme,
  storageKey,
  defaultTheme,
  value,
  themes,
}: UseThemeProviderOptions): UseThemeProps & { themeValues: string[] } {
  const [theme, setThemeState] = useState(() =>
    getTheme(storageKey, defaultTheme),
  );

  const [resolvedTheme, setResolvedTheme] = useState(() =>
    getTheme(storageKey),
  );

  const themeValues: string[] =
    typeof value !== 'undefined' ? Object.values(value) : themes;

  const applyTheme = useCallback(
    (newTheme: string | undefined) => {
      let resolved = newTheme;
      if (typeof resolved === 'undefined') return;

      // If theme is system, resolve it before setting theme
      if (newTheme === DEFAULT_SYSTEM_THEME && enableSystem) {
        resolved = getSystemTheme();
      }

      const name: string | undefined =
        typeof value !== 'undefined' ? value[resolved] : resolved;

      const enableAnimations: VoidFunction | undefined =
        disableTransitionOnChange ? disableAnimation() : undefined;

      const doc = document.documentElement;

      if (attribute === 'class') {
        doc.classList.remove(...themeValues);

        if (typeof name === 'string') doc.classList.add(name);
      } else {
        if (typeof name === 'string') {
          doc.setAttribute(attribute, name);
        } else {
          doc.removeAttribute(attribute);
        }
      }

      if (enableColorScheme) {
        const fallbackScheme =
          COLOR_SCHEMES.includes(defaultTheme) === true ? defaultTheme : null;

        const colorScheme =
          COLOR_SCHEMES.includes(resolved) === true ? resolved : fallbackScheme;

        if (typeof colorScheme === 'string') {
          doc.style.colorScheme = colorScheme;
        }
      }

      // Re-enable transitions if they were disabled
      if (typeof enableAnimations === 'function') {
        enableAnimations();
      }
    },
    [
      attribute,
      defaultTheme,
      disableTransitionOnChange,
      enableColorScheme,
      enableSystem,
      themeValues,
      value,
    ],
  );

  const setTheme = useCallback(
    (newTheme: string) => {
      setThemeState(newTheme);

      // Save to storage
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch (e) {
        console.warn('Error saving theme to localStorage');
      }
    },
    [storageKey],
  );

  const handleMediaQuery = useCallback(
    (mediaQuery: MediaQueryList | MediaQueryListEvent) => {
      const resolved = getSystemTheme(mediaQuery);
      setResolvedTheme(resolved);

      if (
        theme === DEFAULT_SYSTEM_THEME &&
        enableSystem &&
        typeof forcedTheme === 'undefined'
      ) {
        applyTheme(DEFAULT_SYSTEM_THEME);
      }
    },
    [applyTheme, enableSystem, forcedTheme, theme],
  );

  // Always listen to System preference
  useEffect(() => {
    const darkQuery = window.matchMedia(DARK_THEME_MEDIA_QUERY);

    // Intentionally use deprecated listener methods to support iOS & old browsers
    darkQuery.addListener(handleMediaQuery);
    handleMediaQuery(darkQuery);

    return () => {
      darkQuery.removeListener(handleMediaQuery);
    };
  }, [handleMediaQuery]);

  // localStorage event handling
  useEffect(() => {
    const handleStorage = (e: StorageEvent): void => {
      if (e.key !== storageKey) {
        return;
      }

      // If default theme set, use it if localstorage === null (happens on local storage manual deletion)
      const newTheme = e.newValue ?? defaultTheme;
      setTheme(newTheme);
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [defaultTheme, setTheme, storageKey]);

  // Whenever theme or forcedTheme changes, apply it
  useEffect(() => {
    applyTheme(forcedTheme ?? theme);
  }, [applyTheme, forcedTheme, theme]);

  return useMemo(
    () => ({
      theme,
      themeValues,
      setTheme,
      forcedTheme,
      resolvedTheme: theme === DEFAULT_SYSTEM_THEME ? resolvedTheme : theme,
      themes: enableSystem ? [...themes, DEFAULT_SYSTEM_THEME] : themes,
      systemTheme: (enableSystem ? resolvedTheme : undefined) as
        | 'dark'
        | 'light'
        | undefined,
    }),
    [
      enableSystem,
      forcedTheme,
      resolvedTheme,
      setTheme,
      theme,
      themeValues,
      themes,
    ],
  );
}
